// Cross-platform share helper. Prefers the native OS share sheet when
// available (mobile + recent desktop browsers); falls back to copying
// the text to the clipboard and surfacing a toast on desktop.
//
// The Zalo share button drives this with a pre-formatted Vietnamese
// summary text. The "Open Zalo" hint is left to the caller's UI; this
// helper is concerned with the share/copy mechanics only.

export interface ShareOptions {
  title?: string;
  text: string;
  url?: string;
}

export type ShareOutcome =
  | { kind: "shared" }
  | { kind: "copied" }
  | { kind: "cancelled" }
  | { kind: "error"; error: unknown };

/**
 * Check whether `navigator.share` is usable for the given options. We
 * call `canShare()` when available (Safari/Chrome on mobile expose it)
 * so the caller can choose a sensible UI label up front.
 */
export function canNativeShare(options: ShareOptions): boolean {
  if (typeof navigator === "undefined") return false;
  if (typeof navigator.share !== "function") return false;
  // `canShare` not implemented everywhere — when missing we assume the
  // input is shareable (matches the docs' guidance).
  if (typeof navigator.canShare === "function") {
    try {
      const data: ShareData = { text: options.text };
      if (options.title !== undefined) data.title = options.title;
      if (options.url !== undefined) data.url = options.url;
      return navigator.canShare(data);
    } catch {
      return false;
    }
  }
  return true;
}

/**
 * Trigger the share sheet, falling back to clipboard. The promise
 * resolves with an outcome rather than throwing on user cancellation —
 * the call site reacts to "shared" / "copied" / "cancelled" / "error"
 * to show the appropriate toast.
 */
export async function shareOrCopy(options: ShareOptions): Promise<ShareOutcome> {
  if (canNativeShare(options)) {
    try {
      await navigator.share({
        ...(options.title !== undefined ? { title: options.title } : {}),
        text: options.text,
        ...(options.url !== undefined ? { url: options.url } : {}),
      });
      return { kind: "shared" };
    } catch (err: unknown) {
      // AbortError = user dismissed the sheet. Treat as cancelled.
      if (err instanceof DOMException && err.name === "AbortError") {
        return { kind: "cancelled" };
      }
      // Fall through to clipboard on any other error so the user still
      // gets a usable copy of the text.
    }
  }

  return copyToClipboard(options.text);
}

/**
 * Copy a string to the clipboard. Tries the modern async API first,
 * then falls back to a hidden textarea + `execCommand("copy")` for
 * older WebViews (Zalo in-app browser is one we sometimes see).
 */
export async function copyToClipboard(text: string): Promise<ShareOutcome> {
  if (
    typeof navigator !== "undefined" &&
    typeof navigator.clipboard !== "undefined" &&
    typeof navigator.clipboard.writeText === "function"
  ) {
    try {
      await navigator.clipboard.writeText(text);
      return { kind: "copied" };
    } catch (err: unknown) {
      // Permission denied or insecure context — fall through.
      if (!fallbackCopy(text)) {
        return { kind: "error", error: err };
      }
      return { kind: "copied" };
    }
  }
  return fallbackCopy(text) ? { kind: "copied" } : { kind: "error", error: null };
}

function fallbackCopy(text: string): boolean {
  if (typeof document === "undefined") return false;
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}
